/**
 * Records & Certifications — TRUS-562.
 *
 * "Records and Certifications should be one tab and managed based on the type
 * of training/learning scenarios."
 *
 * They were two tabs because they are two tables, but to an administrator they
 * answer one question — what has this person completed, and what are they
 * credentialed for — split only by WHERE the training happened:
 *
 *   • Records are completions of training delivered OUTSIDE the platform. The
 *     evidence is a certificate the org received on paper or by email and
 *     uploads to the record. Amigos works this way.
 *   • Certifications are issued BY the platform for training delivered IN it:
 *     finish the curriculum, the system generates the credential and then
 *     tracks its expiry and renewal. LCC works this way.
 *
 * One tab, two sections, and the copy says which is which — because an admin
 * who uploads a PDF into the wrong one has recorded something that is not true.
 *
 * WHY THE SECTIONS ARE GATED SEPARATELY. The two tabs this replaces had
 * DIFFERENT rules: records was admin-only on `staff_training`, certifications
 * was manager-and-up on `workforce_development`. Collapsing to a single rule
 * would have changed somebody's access in one direction or the other — and
 * gating the merged tab on `staff_training` would have removed Certifications
 * from LCC, who hold workforce_development but not staff_training and are
 * precisely the org that uses in-app certification. So the tab is gated on the
 * wider rule and the records half re-checks the narrower one here. Nobody
 * gains a surface the backend would 403, and nobody loses one they had.
 */

import React, { useMemo, useState } from 'react'
import { Award, ClipboardCheck } from 'lucide-react'
import { useCapability } from '@/hooks/useCapabilities'
import { useRole } from '@/contexts/RoleContext'
import { ADMIN_ROLES } from '@/utils/subTabRegistry'
import RecordPage from './RecordPage'
import WorkforceCertifications from '../certifications/page'

type Section = 'records' | 'certifications'

export default function RecordsAndCertificationsPage() {
  const orgId = Number(localStorage.getItem('active_cbo_id') || 0) || null
  const { enabled: staffTraining } = useCapability(orgId, 'staff_training')
  const { currentRole } = useRole()

  // Mirrors the rule the standalone Records tab carried: ADMIN_ROLES +
  // staff_training. The backend is admin-only on these endpoints
  // (/training-assignments/summary and /training-reports/* return 403 to
  // anyone else), so showing the section to a manager would be a bug, not a
  // permission.
  const canSeeRecords =
    Boolean(staffTraining) && ADMIN_ROLES.includes(String(currentRole || '').toLowerCase())

  const sections = useMemo(() => {
    const out: { key: Section; label: string; hint: string; icon: typeof Award }[] = []
    if (canSeeRecords) {
      out.push({
        key: 'records',
        label: 'Training records',
        hint: 'Completions of training delivered outside WeaveIn. Upload the certificate you were given.',
        icon: ClipboardCheck,
      })
    }
    out.push({
      key: 'certifications',
      label: 'Certifications',
      hint: 'Credentials WeaveIn issued for training delivered in the app, with expiry and renewal.',
      icon: Award,
    })
    return out
  }, [canSeeRecords])

  const [active, setActive] = useState<Section>(() => (canSeeRecords ? 'records' : 'certifications'))

  // An org without staff_training only ever has one section, and a lone
  // switch with nothing to switch to is noise.
  const current = sections.some((s) => s.key === active) ? active : sections[0].key

  return (
    <div className="space-y-4">
      {sections.length > 1 && (
        <div>
          <div className="inline-flex rounded-md border border-v2border bg-surface p-0.5">
            {sections.map((s) => {
              const on = s.key === current
              const Icon = s.icon
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setActive(s.key)}
                  aria-pressed={on}
                  className={`inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-[13px] font-medium transition-colors ${
                    on ? 'bg-v2accent text-white' : 'text-ink-500 hover:bg-paper-2 hover:text-ink'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {s.label}
                </button>
              )
            })}
          </div>
          <p className="mt-2 text-[13px] text-ink-500">
            {sections.find((s) => s.key === current)?.hint}
          </p>
        </div>
      )}

      {current === 'records' ? <RecordPage /> : <WorkforceCertifications />}
    </div>
  )
}
